import type { IocItem, IocType } from './ioc.types'

export interface ReportEntity {
  type: string
  value: string
}

export interface EntityMappingResult {
  iocs: IocItem[]
  // Count of entities dropped because their type isn't an IOC we render
  // (actor names, malware names, etc.) or their value was empty.
  skipped: number
}

// Verity report entity `type` strings → matchboi IocType.
// Keys are normalized (lowercased, non-alphanumerics stripped) so variants
// like "IP Address", "ipv4-addr", "SHA-256" all resolve to the same entry.
// NOTE: Verity's exact `type` vocabulary is still being confirmed against live
// report data (RTD Phase 2 open item). Unknown types are skipped, never guessed;
// extend this table as the real vocabulary is observed.
const ENTITY_TYPE_TO_IOC: Record<string, IocType> = {
  md5: 'MD5',
  sha256: 'SHA256',
  filename: 'FileName',
  file: 'FileName',
  url: 'URL',
  maliciousurl: 'MaliciousURL',
  domain: 'Domain',
  domainname: 'Domain',
  hostname: 'Domain',
  maliciousdomain: 'MaliciousDomain',
  actordomain: 'ActorDomain',
  actorotherwebsite: 'ActorOtherWebsite',
  ip: 'IPAddress',
  ipaddr: 'IPAddress',
  ipaddress: 'IPAddress',
  ipv4: 'IPAddress',
  ipv4addr: 'IPAddress',
  ipv4address: 'IPAddress',
}

const normalizeType = (type: string): string =>
  type.toLowerCase().replace(/[^a-z0-9]/g, '')

/**
 * Turn a report's raw `{ type, value }` entities into matchboi `IocItem`s.
 * Filters out non-IOC entity types, drops empty values, and de-dupes.
 */
export function mapEntitiesToIocs(
  entities: ReportEntity[] | undefined,
): EntityMappingResult {
  const iocs: IocItem[] = []
  const seen = new Set<string>()
  let skipped = 0

  for (const entity of entities ?? []) {
    const value = typeof entity?.value === 'string' ? entity.value.trim() : ''
    const iocType = entity?.type
      ? ENTITY_TYPE_TO_IOC[normalizeType(entity.type)]
      : undefined

    if (!iocType || !value) {
      skipped++
      continue
    }

    const key = `${iocType}:${value}`
    if (seen.has(key)) continue
    seen.add(key)
    iocs.push({ type: iocType, value })
  }

  return { iocs, skipped }
}
